import React from 'react';

import { faChevronLeft, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useListOccupancyStatistics } from 'src/features/statistics/api/useListOccupancyStatistics';
import StatisticsCards from 'src/features/statistics/components/StatisticsCards.tsx';
import StatisticsDropdown from 'src/features/statistics/components/StatisticsDropdown';
import { StatisticsOverviewTooltip } from 'src/features/statistics/components/StatisticsOverviewTooltip';
import { type FormattedTimeframeStatistics, type TimeframeStatistic } from 'src/features/statistics/types';
import { statisticsMessages } from 'src/features/statistics/utils';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { tableMessages } from 'src/registry/components/table';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';

const formatStatisticTimeframe = (name: string | undefined) => {
    if (!name) return undefined;
    const hour = Number(name.split(':')[0]);
    const toHour = ((hour + 1) % 24).toString().padStart(1, '0');
    return `${name} - ${toHour}:00`;
};

export default function OccupancyDuration() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const [selectedSubparkings, setSelectedSubparkings] = React.useState<string[]>([]);
    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();
        const today = new Date(currentDate.getTime());
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const lastWeek = new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).getTime());
        return { afterDate: lastWeek, beforeDate: today };
    });

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(statisticsMessages.timeFrameStatistics));
        return () => removeTitle();
    }, []);

    const { data: subparkingsData } = useListSubParkings({
        parkingId,
    });

    const { data: occupancyStatistics, isLoading } = useListOccupancyStatistics({
        parkingId,
        subparking_ids: selectedSubparkings,
        from_date: dateTime.afterDate.toISOString(),
        to_date: dateTime.beforeDate.toISOString(),
    });

    const { timeframeStatistics, total, averagePerHour } = React.useMemo(() => {
        const result: FormattedTimeframeStatistics = {
            timeframeStatistics: null,
            total: 0,
            averagePerHour: 0,
        };
        if (!occupancyStatistics) return result;
        const totalStatistic = occupancyStatistics.results[occupancyStatistics.count - 1];
        if (!totalStatistic.count_total) return result;
        result.total = totalStatistic.count_total;
        result.averagePerHour = Math.round(totalStatistic.count_total / 24);
        result.timeframeStatistics = Object.entries(totalStatistic.count_by_timeframe).reduce(
            (acc: TimeframeStatistic, [key, value]: [key: string, value: number], index) => {
                const name = `${key.split('_')[2]}:00`;
                const statistic = { name, number: value, tooltip: formatStatisticTimeframe(name) };
                if (index === 0 || acc.min.number > value) {
                    acc.min = statistic;
                }
                if (index === 0 || acc.max.number < value) {
                    acc.max = statistic;
                }
                return { ...acc, data: [...acc.data, statistic] };
            },
            { data: [], min: { name: '', number: 0 }, max: { name: '', number: 0 } },
        );
        return result;
    }, [occupancyStatistics]);

    const cardData = React.useMemo(() => {
        const max = timeframeStatistics?.max?.number;
        const min = timeframeStatistics?.min?.number;

        return [
            { title: formatMessage(statisticsMessages.totalNumberVisitsPeriod), value: total },
            {
                title: formatMessage(statisticsMessages.averageVisitsHour),
                value: averagePerHour,
            },
            {
                title: formatMessage(statisticsMessages.mostActivePeriod),
                subtitle: max
                    ? `${max} ${formatMessage(max === 1 ? statisticsMessages.visit : statisticsMessages.visits).toLowerCase()}`
                    : '',
                value: formatStatisticTimeframe(timeframeStatistics?.max?.name),
            },
            {
                title: formatMessage(statisticsMessages.mostInactivePeriod),
                subtitle: max
                    ? `${min} ${formatMessage(min === 1 ? statisticsMessages.visit : statisticsMessages.visits).toLowerCase()}`
                    : '',
                value: formatStatisticTimeframe(timeframeStatistics?.min?.name),
            },
        ];
    }, [timeframeStatistics, total, averagePerHour]);
    return (
        <SectionBlock>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/statistics/reports">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(statisticsMessages.backToAllReports)}
                </Link>
            </Button>
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(statisticsMessages.timeFrame)}</SectionBlockTitle>
                    <SectionBlockSubtitle>{formatMessage(statisticsMessages.timeFrameSubtitle)}</SectionBlockSubtitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <StatisticsCards cardData={cardData} />
            <SectionBlock>
                <SectionBlockHeader>
                    <SectionBlockTitle>{formatMessage(statisticsMessages.numberEntriesTimeFrame)}</SectionBlockTitle>
                    <div className="flex gap-4">
                        <StatisticsDropdown
                            selectedIds={selectedSubparkings}
                            setSelectedIds={setSelectedSubparkings}
                            data={subparkingsData}
                            title={formatMessage(statisticsMessages.parkingZones)}
                        />
                        <DataTableDateDropdown
                            setValue={setDateTime}
                            inline
                            afterDate={dateTime.afterDate}
                            beforeDate={dateTime.beforeDate}
                        />
                    </div>
                </SectionBlockHeader>
                {total ? (
                    <SectionBlockBody className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={timeframeStatistics?.data}>
                                <CartesianGrid vertical={false} strokeOpacity={0.5} />
                                <Legend />
                                <XAxis dataKey="name" scale="band" xAxisId="values" />
                                <XAxis dataKey="tooltip" style={{ display: 'none' }} />
                                <YAxis
                                    tickCount={10}
                                    tickFormatter={(value: number) => (value === 0 ? '' : `${value}`)}
                                />
                                <Tooltip content={<StatisticsOverviewTooltip />} />
                                <Bar
                                    dataKey="number"
                                    fill="#026AA2"
                                    radius={[6, 6, 0, 0]}
                                    name={formatMessage(statisticsMessages.totalNumber)}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </SectionBlockBody>
                ) : isLoading ? (
                    <Skeleton className="h-96" />
                ) : (
                    <GeneralBlock className="h-96">
                        <GeneralMessageBlock
                            icon={faSearch}
                            title={formatMessage(tableMessages.noResults)}
                            subtitle={formatMessage(tableMessages.noResultsSubtitleFilters)}
                        />
                    </GeneralBlock>
                )}
            </SectionBlock>
        </SectionBlock>
    );
}
